import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import { useState } from 'react'
import {
  BUILT_IN_USER_VALUES,
  getRequirementId,
  isRequirementRef,
  toRequirementRef,
} from '../../../../../util/userRequirement.util'
import { INPUT_KEYS, INPUT_POLICY_PATH, isUserInput } from '../../policy_feature/inputs'

const deriveInitialState = (policyBody) => {
  const autoModeInputs = []
  const requirementIds = {}

  Object.keys(INPUT_POLICY_PATH)
    .filter(isUserInput)
    .forEach((input) => {
      const values = get(policyBody, INPUT_POLICY_PATH[input]) || []
      const references = values.filter(isRequirementRef).map(getRequirementId)
      if (references.length) {
        autoModeInputs.push(input)
        requirementIds[input] = references
      }
    })

  return { autoModeInputs, requirementIds }
}

// Exposed so a caller can seed a field-visibility list with fields that already resolve via a
// stored requirement, without waiting a render for the hook itself to report it.
export const getAutoModeUserInputs = (policyBody) => deriveInitialState(policyBody).autoModeInputs

const hasUsersIncludeBesides = (policyBody, ignoredInput) => {
  const users = policyBody?.conditions?.users

  return Boolean(
    (ignoredInput === INPUT_KEYS.includeUsers ? false : users?.includeUsers?.length) ||
      users?.includeGroups?.length ||
      users?.includeRoles?.length ||
      users?.includeGuestsOrExternalUsers,
  )
}

// Graph rejects a users condition with no include. A template field that is no longer prompted
// and no longer carries a requirement has to land on "No Users" rather than an empty list.
export const includeUsersValueAfterClear = (policyBody) =>
  hasUsersIncludeBesides(policyBody, INPUT_KEYS.includeUsers) ? [] : ['None']

export const ensureUsersInclude = (policyBody) => {
  if (hasUsersIncludeBesides(policyBody)) {
    return policyBody
  }

  const updated = cloneDeep(policyBody) || {}
  set(updated, INPUT_POLICY_PATH[INPUT_KEYS.includeUsers], ['None'])
  return updated
}

// Owns, for a CA template's user-bearing fields, whether each one is resolved automatically from
// one or more stored user requirements (UPN) or still manually prompted at deploy (the
// pre-existing, backward-compatible default) — and keeps the field's raw JSON value (its
// "user:<upn>" references) in sync with whatever is attached. A user requirement is just a UPN
// (like an application requirement's name), so there is no separate definition to own here —
// unlike a named location requirement's type/contents.
export default function useUserFieldRequirements({ initialPolicyBody, policyBody, setPolicyBody }) {
  const [{ autoModeInputs, requirementIds }, setState] = useState(() => deriveInitialState(initialPolicyBody))

  const isAutoMode = (input) => autoModeInputs.includes(input)
  const getAttachedRequirementIds = (input) => requirementIds[input] || []

  // Reads the previous policy body from React's own pending state (the functional setState
  // form) rather than the `policyBody` prop closure, so two calls in the same synchronous pass —
  // e.g. preSelectRequiredInputs auto-attaching both includeUsers and excludeUsers from one
  // copied policy — each build on the other's write instead of both cloning the same stale
  // snapshot and the second one silently overwriting the first's edit.
  const writeFieldValue = (input, values) => {
    setPolicyBody((previousPolicyBody) => {
      const updated = cloneDeep(previousPolicyBody) || {}
      set(updated, INPUT_POLICY_PATH[input], values)
      return updated
    })
  }

  const toggleAutoMode = (input, enabled) => {
    setState((current) => ({
      autoModeInputs: enabled
        ? [...current.autoModeInputs, input]
        : current.autoModeInputs.filter((key) => key !== input),
      requirementIds: enabled ? current.requirementIds : { ...current.requirementIds, [input]: [] },
    }))

    if (!enabled) {
      writeFieldValue(input, input === INPUT_KEYS.includeUsers ? includeUsersValueAfterClear(policyBody) : [])
    }
  }

  // The single source of truth for "which requirements are on this field" — matches the
  // MultiSelectPillList/MultiSelectSearch contract of handing back the whole new selection on
  // every change, add or remove alike.
  const setAttachedRequirementIds = (input, updatedIds, preservedValues) => {
    const currentValues = get(policyBody, INPUT_POLICY_PATH[input]) || []
    let kept = Array.isArray(preservedValues)
      ? preservedValues
      : currentValues.filter((value) => BUILT_IN_USER_VALUES.has(value))
    // Graph rejects an includeUsers condition that mixes "None" with any other value. "None"
    // only ever describes an otherwise-empty field, so attaching a requirement to it drops it —
    // otherwise a technician manually adding the first requirement to a default/cleared
    // includeUsers field would save an invalid template.
    if (input === INPUT_KEYS.includeUsers && updatedIds.length) {
      kept = kept.filter((value) => value !== 'None')
    }
    setState((current) => ({ ...current, requirementIds: { ...current.requirementIds, [input]: updatedIds } }))
    writeFieldValue(input, [...kept, ...updatedIds.map(toRequirementRef)])
  }

  const hydrate = (nextPolicyBody) => {
    setState(deriveInitialState(nextPolicyBody))
  }

  return { isAutoMode, getAttachedRequirementIds, toggleAutoMode, setAttachedRequirementIds, hydrate }
}
